import { TFunction } from 'i18next';

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * DOM ids of the four Approval-drawer sections the anchor navigates between
 * (NA-5579 AC1). They double as the anchor `href` targets, so they must stay
 * unique within the drawer and valid as CSS id selectors.
 */
export enum AnchorSectionKey {
  BASIC_INFO = 'payment-detail-basic-info',
  APPLICATION_CONTENT = 'payment-detail-application-content',
  TRANSACTIONS_DETAIL = 'payment-detail-transactions-detail',
  JOURNAL = 'payment-detail-journal',
}

export const DEFAULT_ANCHOR = `#${AnchorSectionKey.BASIC_INFO}`;

export type AnchorItem = {
  key: string;
  title: string;
  href: string;
};

/**
 * Anchor items reuse the same translation keys as the section headings, so the
 * rail label and the section title can never drift apart.
 */
export const getAnchorItems = (t: TFunction): AnchorItem[] => [
  {
    key: AnchorSectionKey.BASIC_INFO,
    title: t('approving_invoice_report_detail_basic_info'),
    href: `#${AnchorSectionKey.BASIC_INFO}`,
  },
  {
    key: AnchorSectionKey.APPLICATION_CONTENT,
    title: t('application_content.section_title'),
    href: `#${AnchorSectionKey.APPLICATION_CONTENT}`,
  },
  {
    key: AnchorSectionKey.TRANSACTIONS_DETAIL,
    title: t('approving_invoice_reports.transactions.title'),
    href: `#${AnchorSectionKey.TRANSACTIONS_DETAIL}`,
  },
  {
    key: AnchorSectionKey.JOURNAL,
    title: t('journal_entry.section_title'),
    href: `#${AnchorSectionKey.JOURNAL}`,
  },
];

/**
 * Marks the Approval drawer's left-pane scroll area.
 *
 * Deep children (the 支払明細 layout switch) need that element to keep the
 * scroll position stable across a layout change; a data attribute keeps them
 * from having to thread a ref through every level in between.
 */
export const SCROLL_AREA_ATTRIBUTE = 'data-payment-detail-scroll-area';

/* -------------------------------------------------------------------------- */
/* Scroll tuning                                                               */
/* -------------------------------------------------------------------------- */

/** Gap kept between the scroll container top and the section a click targets. */
export const SCROLL_OFFSET = 20;

/** Time without any scroll movement before a smooth scroll counts as finished. */
export const SCROLL_IDLE_THRESHOLD_MS = 150;

/** Pixel tolerance when comparing a scroll position against its target. */
export const SCROLL_POSITION_TOLERANCE = 2;

/** Minimum delta that counts as the user taking over an in-flight smooth scroll. */
export const MANUAL_SCROLL_THRESHOLD = 10;

/** How long scroll detection stays muted after the drawer resets its position. */
export const RESET_SETTLE_MS = 300;

/* -------------------------------------------------------------------------- */
/* Scroll math (pure — no React, no module state)                              */
/* -------------------------------------------------------------------------- */

export const getAnchorHrefs = (items: AnchorItem[]): string[] =>
  items.map((item) => item.href);

export const getMaxScrollTop = (scrollContainer: HTMLElement): number =>
  Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);

/**
 * Scroll position that puts `section` just below the top of `scrollContainer`,
 * clamped to the scrollable range.
 *
 * Deliberately derived from the live rects rather than `offsetTop` so it stays
 * correct while a smooth scroll is still running and regardless of which
 * ancestor happens to be the section's offset parent.
 */
export const getScrollTopForSection = (
  scrollContainer: HTMLElement,
  section: HTMLElement
): number => {
  const distanceFromContainerTop =
    section.getBoundingClientRect().top -
    scrollContainer.getBoundingClientRect().top;
  const target =
    scrollContainer.scrollTop + distanceFromContainerTop - SCROLL_OFFSET;

  return Math.min(Math.max(0, target), getMaxScrollTop(scrollContainer));
};

/**
 * The href of the section currently at the top of the viewport — the last one
 * whose top edge has already passed the click offset.
 *
 * Section-boundary based rather than "split the scroll range evenly per
 * section": 支払明細 can hold thousands of rows (NA-5579 context), so equal
 * division would highlight an anchor that has nothing to do with what is on
 * screen.
 */
export const getActiveHref = (
  scrollContainer: HTMLElement,
  sectionRoot: HTMLElement,
  hrefs: string[]
): string | null => {
  if (hrefs.length === 0) {
    return null;
  }

  // At the very bottom the last section can never reach the top offset, so it
  // would otherwise be impossible to highlight.
  if (
    scrollContainer.scrollTop >=
    getMaxScrollTop(scrollContainer) - SCROLL_POSITION_TOLERANCE
  ) {
    return hrefs[hrefs.length - 1] ?? null;
  }

  const containerTop = scrollContainer.getBoundingClientRect().top;
  let activeHref = hrefs[0] ?? null;

  hrefs.forEach((href) => {
    const section = sectionRoot.querySelector(href);
    if (!(section instanceof HTMLElement)) {
      return;
    }
    const distanceFromContainerTop =
      section.getBoundingClientRect().top - containerTop;
    if (distanceFromContainerTop - SCROLL_OFFSET <= SCROLL_POSITION_TOLERANCE) {
      activeHref = href;
    }
  });

  return activeHref;
};
